import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Scales, FileText, Robot, Clock, ChartLineUp, Upload, Check, ArrowRight } from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/14535680/pexels-photo-14535680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRUST_IMG = "https://images.pexels.com/photos/7841445/pexels-photo-7841445.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A192F]">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <Scales weight="duotone" size={24} className="text-[#1A2238] sm:hidden" />
            <Scales weight="duotone" size={28} className="text-[#1A2238] hidden sm:block" />
            <span className="font-serif text-xl sm:text-2xl tracking-tight">Lex<span className="text-[#D32F2F]">Auto</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-sm">
            <a href="#funktionen" className="hover:text-[#D32F2F] transition-colors">Funktionen</a>
            <a href="#workflow" className="hover:text-[#D32F2F] transition-colors">Workflow</a>
            <a href="#preise" className="hover:text-[#D32F2F] transition-colors">Für Kanzleien</a>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/login")} className="text-sm px-2.5 sm:px-4" data-testid="nav-login-btn">Anmelden</Button>
            <Button size="sm" onClick={() => nav("/register")} className="bg-[#1A2238] hover:bg-[#2C3859] text-sm px-3 sm:px-4" data-testid="nav-register-btn">
              <span className="hidden sm:inline">Kostenlos testen</span>
              <span className="sm:hidden">Testen</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline text-[#D32F2F] mb-4 sm:mb-6">Kanzleisoftware · Verkehrsrecht</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] sm:leading-[0.95] mb-6 sm:mb-8">
              Akten in <span className="italic">60 Sekunden.</span><br />
              Nicht in 60&nbsp;Minuten.
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-[#4B5563] max-w-2xl mb-8 sm:mb-10 leading-relaxed">
              Gutachten per Drag &amp; Drop einlesen. KI extrahiert Schadenshöhe, Wertminderung und Beteiligte.
              Schriftsätze, Bezifferung und Fristen — automatisch. Für Verkehrsrechtsanwälte, die Zeit in Mandate investieren wollen, nicht in Tabellen.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
              <Button size="lg" onClick={() => nav("/register")} className="bg-[#1A2238] hover:bg-[#2C3859] h-12 px-6 sm:px-8 w-full sm:w-auto" data-testid="hero-cta-register">
                Kanzlei kostenlos einrichten <ArrowRight className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => nav("/login")} className="h-12 px-6 sm:px-8 border-[#1A2238] w-full sm:w-auto" data-testid="hero-cta-login">
                Demo ansehen
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-[#4B5563]">
              <span className="flex items-center gap-2"><Check size={14} className="text-[#16A34A]" /> DSGVO-konform</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#16A34A]" /> Hosting in Deutschland</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#16A34A]" /> RVG-konforme Bezifferung</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative mt-8 lg:mt-0">
            <div className="relative rounded-lg overflow-hidden border border-[#E5E7EB]">
              <img src={HERO_IMG} alt="Autobahn" className="w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-[#0A192F]/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="overline text-white/80 mb-2">Live Beispiel</p>
                <p className="font-serif text-xl sm:text-2xl leading-tight">„Anspruchsschreiben erstellt in 47&nbsp;Sekunden."</p>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 sm:absolute sm:-bottom-6 sm:-left-6 bg-white border border-[#E5E7EB] rounded-lg p-5 shadow-sm sm:w-64">
              <p className="overline text-[#9CA3AF] mb-2">Zeitersparnis</p>
              <p className="font-serif text-4xl text-[#1A2238]">– 87%</p>
              <p className="text-xs text-[#4B5563] mt-1">pro Akte gegenüber manueller Bearbeitung</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Bento */}
      <section id="funktionen" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="mb-10 sm:mb-16 max-w-3xl">
          <p className="overline text-[#D32F2F] mb-4">Was die Software kann</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight sm:leading-none">
            Vom Gutachten bis zur Klageschrift — <span className="italic">ein Klick.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-6">
          <FeatureCard
            className="md:col-span-3"
            icon={<Upload weight="duotone" size={32} />}
            title="Gutachten einlesen"
            desc="PDF per Drag &amp; Drop. KI erkennt Reparaturkosten, Wertminderung, Wiederbeschaffungswert, Kennzeichen und Versicherung — automatisch."
          />
          <FeatureCard
            className="md:col-span-3"
            icon={<Robot weight="duotone" size={32} />}
            title="KI-Schriftsätze"
            desc="Anspruchsschreiben, Unfallhergang, Klageschrift, OWi-Einspruch. Claude Sonnet 4.5 formuliert im deutschen Anwaltsstil."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<ChartLineUp weight="duotone" size={32} />}
            title="RVG-Bezifferung"
            desc="Streitwert, Geschäftsgebühr, Nutzungsausfall nach Sanden/Danner — korrekt."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<FileText weight="duotone" size={32} />}
            title="OWi-Modul"
            desc="Bußgeldbescheid hochladen, Einspruch generiert, Frist im Blick."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<Clock weight="duotone" size={32} />}
            title="Fristenkontrolle"
            desc="14-Tage-Einspruch, Verjährung, Wiedervorlagen. Nie wieder verpasst."
          />
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-[#D32F2F] mb-4">Workflow</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight sm:leading-none mb-6">
              Sechs Schritte.<br /><span className="italic">Unter einer Minute.</span>
            </h2>
            <p className="text-[#4B5563] leading-relaxed mb-8">
              Von der E-Mail des Sachverständigen bis zum versandfertigen Schriftsatz an die Versicherung.
              Ein Bildschirm. Ein Workflow. Keine Medienbrüche.
            </p>
            <img src={TRUST_IMG} alt="Anwältin" className="rounded-lg border border-[#E5E7EB] w-full object-cover aspect-[4/3]" />
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-5 sm:space-y-6">
              {[
                ["01", "Gutachten hochladen", "Drag &amp; Drop oder E-Mail-Weiterleitung."],
                ["02", "KI extrahiert Daten", "Kennzeichen, Kosten, Versicherung, Schadennr."],
                ["03", "Akte wird erstellt", "Mandant, Gegner, Versicherung automatisch angelegt."],
                ["04", "Bezifferung berechnet", "Streitwert, RVG-Gebühr, Nutzungsausfall."],
                ["05", "Schriftsatz generiert", "Anspruchsschreiben im deutschen Anwaltsstil."],
                ["06", "Versand an Versicherung", "Per E-Mail oder DATEV-Export (optional)."],
              ].map(([n, t, d]) => (
                <li key={n} className="group flex gap-4 sm:gap-6 items-start pb-5 sm:pb-6 border-b border-[#E5E7EB] last:border-0">
                  <span className="font-mono text-xs text-[#9CA3AF] pt-2 tracking-widest">{n}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl mb-1">{t}</h3>
                    <p className="text-[#4B5563]" dangerouslySetInnerHTML={{ __html: d }} />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="preise" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="bg-[#1A2238] text-white rounded-lg p-8 sm:p-12 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#D32F2F]/10 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div>
              <p className="overline text-[#D32F2F] mb-3 sm:mb-4">Kostenlos starten</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight sm:leading-none">
                Ihre nächste Akte<br />in <span className="italic">60 Sekunden</span> fertig.
              </h2>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3 sm:gap-4">
              <Button size="lg" onClick={() => nav("/register")} className="bg-white text-[#1A2238] hover:bg-white/90 h-12 px-6 sm:px-8 w-full sm:w-auto" data-testid="cta-register-bottom">
                Jetzt kostenlos einrichten <ArrowRight className="ml-2" />
              </Button>
              <p className="text-xs sm:text-sm text-white/60">Keine Kreditkarte · 14 Tage Testphase</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB] py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between gap-3 sm:gap-4 text-xs sm:text-sm text-[#4B5563]">
          <p>© 2026 LexAuto · Kanzleisoftware Verkehrsrecht</p>
          <p>DSGVO · Impressum · Datenschutz</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ className = "", icon, title, desc }) {
  return (
    <div className={`group bg-white border border-[#E5E7EB] rounded-lg p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${className}`}>
      <div className="text-[#1A2238] mb-6 group-hover:text-[#D32F2F] transition-colors">{icon}</div>
      <h3 className="font-serif text-2xl mb-3">{title}</h3>
      <p className="text-[#4B5563] leading-relaxed" dangerouslySetInnerHTML={{ __html: desc }} />
    </div>
  );
}
